export class ErrorMessage {
    public title: string;

    constructor(title: string) {
        this.title = title;
    }
}

export type ErrorCodeMap = Record<string, ErrorMessage>;

export enum ErrorType {
    BulkError = 'Could not update orders!',
    OrderUpdate = 'Could not update order!',
    ActivateInvoice = 'Could not activate order!',
    LoadUsers = 'Could not load users!',
    LoadPendingUsers = 'Could not load pending users!',
    DeletePendingUser = 'Could not delete pending user!',
    LoadGroups = 'Group details error!',
    DeleteGroupUser = 'Could not delete user from group!',
    Order = 'Could not get Order Details',
    Account = 'Could not get Account Details',
    Buyer = 'Could not get Buyer Details',
    FetchAccount = 'Unable to load account!',
    RedirectInvite = 'Could not redirect invite!',
    Search = 'Could not perform search!',
    CancelOrder = 'Could not cancel order!',
    Extend = 'Could not extend!',
    Invite = 'Could not invite user!',
    Validation = 'Validation error!',
    ResendInvoice = 'Could not resend invoice',
    ResendWebhook = 'Could not resend webhook!',
    DeleteWebhook = 'Could not delete webhook!',
    CreditFee = 'Could not credit selected fees',
    SingleDownload = 'Could not download file, please try again later.',
    ChangeInvoiceAddress = 'Could not change invoice address',
    DashboardAggregate = 'Could not show data',
    Customer = 'Could not get customer',
    ApiSecret = 'Something went wrong with the api keys',
    RemoveApiSecret = 'Could not remove api key. Please try again later.',
    CreatePaylink = 'Something went wrong when creating the pay link!',
    CreateAdvanceInvoice = 'Something went wrong when creating the advance invoice!',
    CreateNoFraudInvoice = 'Something went wrong when creating the B2B invoice!',
    CreateServiceInvoice = 'Something went wrong when creating the service invoice!',
    CreateInvoice = 'Something went wrong when creating the invoice!',
    SendPaylink = 'Could not send pay link. Please try again later.',
    DeletePaylink = 'Could not delete pay link.',
    LoadStore = 'Store could not be loaded',
    PatchStore = 'Store could not be changed',
    SearchPaylink = 'Could not perform pay link search!',
    PaylinkDetails = 'Could not show Pay Link details.',
    EnableCheckoutApi = 'Could not enable checkout api.',
    DeleteOrganization = 'Could not delete organization',
    CreateOrganization = 'Could not create organization',
    AddMerchantToOrganization = 'Organization was created but the merchant could not be added',
    InviteToCreatedOrganization = 'Organization was created but the invitation could not be sent',
    GenerateReport = 'Could not create report',
    LoadReports = 'Could not get reports',
    Webhook = 'Something went wrong with the webhooks',
    FailedWebhook = 'Something went wrong loading the failed webhooks',
    UpdatePaylinkOrderNumber = 'Could not update reference. Please try again later.',
    UpdateOrderNumber = 'Could not update order reference. Please try again later.',
    B2BAddBuyer = 'Could not add buyer',
    OnboardStore = 'The store could not be onboarded',
    LoadMerchants = 'Could not load merchants',
    AddMerchant = 'Could not add the merchant',
    ApproveMerchant = 'Could not approve merchant',
    SaveMerchant = 'Could not save the merchant',
    StartKyc = 'Could not open KYC',
    StartBankAccountVerification = 'Could not open bank account verification',
    LoadBankAccounts = 'Could not load the verified bank accounts',
    LoadMerchantPrices = 'Could not load the price list',
    SaveMerchantPrices = 'Could not save the price list',
    SyncMerchantPrices = 'Could not sync prices',
    LoadAgreementSignatories = 'Could not load who may sign for the merchant',
    SendAgreement = 'Could not send the agreement',
}

const errorMap: ErrorCodeMap = {
    ONBOARDING_BANK_ACCOUNT_REQUIRED: new ErrorMessage(ErrorType.OnboardStore),
    ONBOARDING_BANK_ACCOUNT_NOT_VERIFIED: new ErrorMessage(ErrorType.OnboardStore),
    MERCHANT_ALLOWED_URLS_REQUIRED: new ErrorMessage(ErrorType.SaveMerchant),
    MERCHANT_ALLOWED_URL_INVALID: new ErrorMessage(ErrorType.SaveMerchant),
    MERCHANT_PRICE_DUPLICATE: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_COUNTRY_NOT_SUPPORTED: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_SALES_SEGMENT_NOT_SUPPORTED: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_NEGATIVE: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_PERCENTAGE_OUT_OF_RANGE: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_EMPTY: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_PRODUCT_CODE_INVALID: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_PRODUCT_TYPE_REQUIRED: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_CURRENCY_MISMATCH: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_VALID_PERIOD_INVALID: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_METHOD_UNKNOWN: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_METHOD_NOT_IN_SEGMENT: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_METHOD_WITHOUT_PRODUCTS: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_RECURRING_NOT_SUPPORTED: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_VALIDITY_NOT_SUPPORTED: new ErrorMessage(ErrorType.SaveMerchantPrices),
    MERCHANT_PRICE_SYNC_NOT_AVAILABLE: new ErrorMessage(ErrorType.SyncMerchantPrices),
    MERCHANT_PRICE_SYNC_NO_EDGE_MERCHANT: new ErrorMessage(ErrorType.SyncMerchantPrices),
    MERCHANT_AGREEMENT_SIGNATORIES_UNAVAILABLE: new ErrorMessage(ErrorType.SendAgreement),
    MERCHANT_AGREEMENT_SIGNATORY_REQUIRED: new ErrorMessage(ErrorType.SendAgreement),
    MERCHANT_AGREEMENT_SIGNATORY_NAME_REQUIRED: new ErrorMessage(ErrorType.SendAgreement),
    MERCHANT_AGREEMENT_SIGNATORY_EMAIL_INVALID: new ErrorMessage(ErrorType.SendAgreement),
    MERCHANT_AGREEMENT_SIGNATORY_DUPLICATED: new ErrorMessage(ErrorType.SendAgreement),
    MERCHANT_AGREEMENT_SIGNATORIES_NOT_A_SIGNING_COMBINATION: new ErrorMessage(
        ErrorType.SendAgreement
    ),
    MERCHANT_AGREEMENT_PROVIDER_UNAVAILABLE: new ErrorMessage(ErrorType.SendAgreement),
    ADJUST_INVALID_TRANSACTION_AMOUNT: new ErrorMessage(ErrorType.OrderUpdate),
    REPLACE_INVALID_TRANSACTION_AMOUNT: new ErrorMessage(ErrorType.OrderUpdate),
    REPLACE_TOTAL_AMOUNT_MUST_BE_POSITIVE: new ErrorMessage(ErrorType.OrderUpdate),
    EDIT_ACCOUNT_TOTAL_AMOUNT_MUST_BE_POSITIVE: new ErrorMessage(ErrorType.OrderUpdate),
    REPLACE_INVOICE_NOT_FOUND: new ErrorMessage(ErrorType.OrderUpdate),
    ACTIVATE_INVOICE_NOT_FOUND: new ErrorMessage(ErrorType.ActivateInvoice),
    USER_MUST_HAVE_GROUP: new ErrorMessage(ErrorType.DeleteGroupUser),
};

export default errorMap;
